import React from 'react';

import { renderTrademarkText } from '../TrademarkText';

export type SemanticHeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
export interface SemanticHeadingData {
  text?: string;
  tag?: SemanticHeadingTag;
}

export interface SemanticHeadingProps {
  heading?: SemanticHeadingData;
  fallbackText?: string;
  fallbackTag?: SemanticHeadingTag;
  className?: string;
  children?: React.ReactNode;
}

export const HeadingTag = ({
  tag,
  className,
  children,
}: {
  tag?: SemanticHeadingTag;
  className?: string;
  children?: React.ReactNode;
}) => {
  switch (tag) {
    case 'h1': return <h1 className={className}>{children}</h1>;
    case 'h2': return <h2 className={className}>{children}</h2>;
    case 'h3': return <h3 className={className}>{children}</h3>;
    case 'h4': return <h4 className={className}>{children}</h4>;
    case 'h5': return <h5 className={className}>{children}</h5>;
    case 'h6': return <h6 className={className}>{children}</h6>;
    default: return <span className={className}>{children}</span>;
  }
};

/**
 * SemanticHeading component renders heading text with configurable HTML tags.
 *
 * Supports both new semantic heading data and legacy field fallbacks for
 * backward compatibility during migration.
 *
 * @param heading - New semantic heading data with text and tag
 * @param fallbackText - Legacy heading text for backward compatibility
 * @param fallbackTag - Default HTML tag if none specified (defaults to SemanticHeadingTag.H2)
 * @param className - CSS classes to apply to the heading element
 * @param children - Optional children to render instead of text content
 */
export const SemanticHeading: React.FC<SemanticHeadingProps> = ({
  heading,
  fallbackText,
  fallbackTag = 'h2',
  className,
  children,
}) => {
  // Use heading data if available, otherwise fall back to legacy values
  const text = children || heading?.text || fallbackText;
  const tag = heading?.tag || fallbackTag;

  // If no text content and no children, don't render anything
  if (!text && !children) return null;

  return (
    <HeadingTag className={className} tag={tag}>
      {typeof text === 'string' ? renderTrademarkText(text) : text}
    </HeadingTag>
  );
};

export default SemanticHeading;
